import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './Profile.css';
import ConfirmDialog from '../confirmDialog';

const Profile = ({ onBack, user, merchantInfo, merchantInfoError, onLogout, onDeleteAccount, onManageStore, onLoadMerchantInfo }) => {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 组件挂载时加载店铺信息
  useEffect(() => {
    if (user && !merchantInfo && !merchantInfoError && onLoadMerchantInfo) {
      onLoadMerchantInfo();
    }
  }, [user, merchantInfo, merchantInfoError, onLoadMerchantInfo]);

  const handleManageStoreClick = () => {
    if (onManageStore) {
      onManageStore();
    }
  };

  const handleLogoutClick = () => {
    if (onLogout) {
      onLogout();
    }
    // Return to previous page after logout
    if (onBack) {
      onBack();
    }
  };

  const handleDeleteAccountClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (onDeleteAccount) {
      onDeleteAccount();
    }
    setShowDeleteConfirm(false);
  };

  // Profile sections
  const profileSections = [
    {
      title: t('profile.accountInfo'),
      items: [
        {
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          ),
          title: t('profile.personalInfo'),
          description: user ? user.name : t('profile.notLoggedIn'),
          action: (
            <button className="profile-action-btn">
              {user ? t('profile.edit') : t('profile.login')}
            </button>
          )
        }
      ]
    },
    {
      title: t('profile.myStore'),
      items: [
        {
          storeInfo: true,
          action: (
            <button className="profile-action-btn" onClick={handleManageStoreClick}>
              {t('profile.manage')}
            </button>
          )
        }
      ]
    },
    {
      title: t('profile.accountManagement'),
      items: [
        {
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          ),
          title: t('profile.logout'),
          description: t('profile.logoutDescription'),
          action: (
            <button className="profile-action-btn profile-action-btn-danger" onClick={handleLogoutClick}>
              {t('profile.logout')}
            </button>
          ),
          show: user
        },
        {
          icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18"></path>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          ),
          title: t('profile.deleteAccount'),
          description: t('profile.deleteAccountDescription'),
          action: (
            <button className="profile-action-btn profile-action-btn-danger" onClick={handleDeleteAccountClick}>
              {t('profile.delete')}
            </button>
          ),
          show: user
        }
      ]
    }
  ];

  return (
    <div className="profile-container">
      {/* Header */}
      <div className="profile-header">
        <button 
          className="profile-back-btn" 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onBack) {
              onBack();
            }
          }}
          type="button"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <h1 className="profile-title">{t('profile.title')}</h1>
      </div>

      {/* User Avatar Section */}
      <div className="profile-avatar-section">
        <div className="profile-avatar">
          {user ? (
            user.avatar ? (
              <img src={user.avatar} alt={user.name} />
            ) : (
              <div className="profile-avatar-placeholder">
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
            )
          ) : (
            <div className="profile-avatar-placeholder">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
          )}
        </div>
        <div className="profile-user-info">
          <h2 className="profile-user-name">{user ? user.userId || t('profile.user') : t('profile.notLoggedIn')}</h2>
          {user && <p className="profile-user-status">{t('profile.loggedIn')}</p>}
        </div>
      </div>

      {/* Content */}
      <div className="profile-content">
        {profileSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="profile-section">
            <div className="profile-section-header">
              <h2 className="profile-section-title">{section.title}</h2>
            </div>
            {section.items.map((item, itemIndex) => {
              // Skip items that should not be shown
              if (item.show === false) {
                return null;
              }
              
              // Special rendering for store info
              if (item.storeInfo) {
                // 如果用户已登录但店铺信息未加载，显示加载状态
                if (user && !merchantInfo && !merchantInfoError) {
                  return (
                    <div key={itemIndex} className="profile-store-item">
                      <div className="profile-store-image">
                        <div className="profile-store-image-placeholder"></div>
                      </div>
                      <div className="profile-store-info">
                        <h3 className="profile-store-name">{t('profile.loadingStoreInfo')}</h3>
                        <div className="profile-store-status">
                          <span className="status-indicator status-loading"></span>
                          <span>{t('profile.loading')}</span>
                        </div>
                      </div>
                      <div className="profile-store-action">
                        <div className="profile-action-btn disabled">{t('profile.manage')}</div>
                      </div>
                    </div>
                  );
                }
                
                // 如果用户已登录但店铺信息加载失败，显示加载失败状态
                if (user && !merchantInfo && merchantInfoError) {
                  return (
                    <div key={itemIndex} className="profile-store-item">
                      <div className="profile-store-image">
                        <div className="profile-store-image-placeholder error"></div>
                      </div>
                      <div className="profile-store-info">
                        <h3 className="profile-store-name">{t('profile.loadStoreInfoFailed')}</h3>
                        <div className="profile-store-status">
                          <span className="status-indicator status-error"></span>
                          <span>{t('profile.loadFailed')}</span>
                        </div>
                      </div>
                      <div className="profile-store-action">
                        <button 
                          className="profile-action-btn refresh-btn"
                          onClick={onLoadMerchantInfo}
                        >
                          {t('profile.refresh')}
                        </button>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div key={itemIndex} className="profile-store-item">
                    <div className="profile-store-image">
                      <img src={merchantInfo?.store_image || "/images/store-placeholder.png"} alt="Store" />
                    </div>
                    <div className="profile-store-info">
                      <h3 className="profile-store-name">{merchantInfo?.store_name || "示例店铺"}</h3>
                      <div className="profile-store-status">
                        <span className={`status-indicator ${merchantInfo?.is_open ? 'status-open' : 'status-closed'}`}></span>
                        <span>{merchantInfo?.is_open ? t('profile.storeOpen') : t('profile.storeClosed')}</span>
                      </div>
                    </div>
                    <div className="profile-store-action">
                      <div className="profile-store-action-container">
                        {item.action}
                      </div>
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={itemIndex} className="profile-item">
                  <div className="profile-item-icon">
                    {item.icon}
                  </div>
                  <div className="profile-item-content">
                    <h3 className="profile-item-title">{item.title}</h3>
                    <p className="profile-item-description">{item.description}</p>
                  </div>
                  <div className="profile-item-action">
                    {item.action}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDialog.deleteAccountTitle')}
        message={t('confirmDialog.deleteAccountMessage')}
        confirmText={t('confirmDialog.deleteAccountConfirm')}
        cancelText={t('confirmDialog.cancel')}
        type="danger"
      />
    </div>
  );
};

export default Profile;